import React, { useRef } from "react";
import "./DragAndDrop.scss";

/**
 *
 * Props:
 *  lists      → [{ id, title, items: [{ id, ...resto }] }]
 *  onChange   → (listsActualizadas) => void   — estado actualizado tras mover
 *  onItemMove → (item, fromListId, toListId) => void  — para cambiar estatus
 *  renderItem → (item) => ReactNode            — render personalizado del item
 */
const DragAndDrop = ({ lists, onChange, onItemMove, renderItem, className }) => {

    // Guardamos qué ítem se está arrastrando y desde qué lista viene
    const arrastre = useRef({ item: null, fromListId: null });

    // Al iniciar el arrastre, almacenamos el ítem y su lista de origen
    const alIniciarArrastre = (item, fromListId) => {
        arrastre.current = { item, fromListId };
    };

    // Al soltar sobre una lista destino, movemos el ítem
    const alSoltar = (toListId) => {
        const { item, fromListId } = arrastre.current;

        // Si no hay ítem o se soltó en la misma lista, no hacemos nada
        if (!item || fromListId === toListId) return;

        // Construimos las listas actualizadas:
        // - quitamos el ítem de la lista origen
        // - lo agregamos al final de la lista destino
        const listsActualizadas = lists.map((lista) => {
            if (lista.id === fromListId) {
                return { ...lista, items: lista.items.filter((i) => i.id !== item.id) };
            }
            if (lista.id === toListId) {
                return { ...lista, items: [...lista.items, item] };
            }
            return lista;
        });

        onChange?.(listsActualizadas);               // actualizamos el estado del padre
        onItemMove?.(item, fromListId, toListId);    // notificamos el cambio de estatus
        arrastre.current = { item: null, fromListId: null }; // limpiamos la referencia
    };

    // Necesario para que el navegador permita hacer drop en el área
    const permitirDrop = (e) => e.preventDefault();

    return (
        <div className={`drag-and-drop ${className || ""}`}>
            {lists.map((lista) => (
                <div
                    key={lista.id}
                    className={`dnd-list ${lista.className}`}
                    onDragOver={permitirDrop}
                    onDrop={() => alSoltar(lista.id)}
                >
                    {/* Encabezado de la lista con título y contador */}
                    <div className="dnd-list-header">
                        <span className="dnd-list-title">{lista.title}</span>
                        <span className="dnd-list-count">{lista.items.length}</span>
                    </div>

                    {/* Ítems arrastrables */}
                    <div className="dnd-items">
                        {lista.items.map((item) => (
                            <div
                                key={item.id}
                                className="dnd-item"
                                draggable
                                onDragStart={() => alIniciarArrastre(item, lista.id)}
                            >
                                {renderItem ? renderItem(item) : (
                                    <span>{item.label ?? item.id}</span>
                                )}
                            </div>
                        ))}

                        {/* Placeholder cuando la lista está vacía */}
                        {lista.items.length === 0 && (
                            <div className="dnd-empty">Sin elementos</div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default DragAndDrop;
